import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { PartnerDealCommitteeQueueItemResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { PartnerDealCommitteeHttpService } from '@services/http/partners/partner-deal-committee-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { forkJoin } from 'rxjs';
import {
  CreditCommitteeQueueFilter,
  computeQueueKpis,
  describeQueueEmpty,
  describeQueueSituation,
  describeQueueVotes,
  filterQueue,
  isAwaitingMyVote,
} from '@app/features/dashboard/commercial/pages/credit-committee/credit-committee-presenter';

/**
 * Fila do comitê de crédito (card 3789), em Comercial › Comitê de crédito: os deals em votação e os
 * decididos nos últimos 30 dias, com o que espera o voto de quem está vendo. A linha abre o deal na
 * etapa Comitê (`?stage=committee`), onde se vota.
 *
 * Só o time interno (`PARTNERDEALCOMMITTEE_VIEW`). Tela de leitura: as contagens e a situação de
 * cada linha vêm prontas do servidor; o `credit-committee-presenter` só as transforma em texto.
 */
@Component({
  selector: 'app-credit-committee-queue',
  standalone: true,
  imports: [CommonModule, RouterLink, SharedModule],
  templateUrl: './credit-committee-queue.component.html',
  styleUrl: './credit-committee-queue.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditCommitteeQueueComponent implements OnInit {
  private readonly committeeHttp = inject(PartnerDealCommitteeHttpService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Comitê de crédito' },
  ];
  protected readonly home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
  };

  protected readonly canConfigure = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerDealCommittee.CONFIGURE),
    { initialValue: false }
  );

  protected readonly openItems = signal<PartnerDealCommitteeQueueItemResponse[]>([]);
  protected readonly decidedItems = signal<PartnerDealCommitteeQueueItemResponse[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly filter = signal<CreditCommitteeQueueFilter>('open');

  protected readonly kpis = computed(() => computeQueueKpis(this.openItems(), this.decidedItems()));
  protected readonly rows = computed(() =>
    filterQueue(this.filter(), this.openItems(), this.decidedItems())
  );
  protected readonly emptyText = computed(() => describeQueueEmpty(this.filter()));

  protected readonly filters: { value: CreditCommitteeQueueFilter; label: string }[] = [
    { value: 'open', label: 'Em comitê' },
    { value: 'mine', label: 'Aguardando meu voto' },
    { value: 'decided', label: 'Decididos' },
  ];

  protected readonly situation = describeQueueSituation;
  protected readonly votes = describeQueueVotes;
  protected readonly awaitingMyVote = isAwaitingMyVote;

  ngOnInit(): void {
    this.load();
  }

  /** Lê as duas listas: em comitê e decididos nos últimos 30 dias. */
  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    forkJoin({
      open: this.committeeHttp.getQueue('open'),
      decided: this.committeeHttp.getQueue('decided'),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ open, decided }) => {
          this.openItems.set(open);
          this.decidedItems.set(decided);
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          this.error.set(
            err?.errors?.[0]?.message ??
              'Não foi possível carregar a fila do comitê agora. Clique em Tentar de novo.'
          );
        },
      });
  }

  /** Quantos deals cada filtro mostra, para o rótulo do botão. */
  protected countFor(filter: CreditCommitteeQueueFilter): number {
    return filterQueue(filter, this.openItems(), this.decidedItems()).length;
  }

  protected selectFilter(filter: CreditCommitteeQueueFilter): void {
    this.filter.set(filter);
  }

  /** Rota do deal na etapa Comitê. */
  protected dealRoute(item: PartnerDealCommitteeQueueItemResponse): string[] {
    return ['/dashboard/commercial/my-deals', item.dealId];
  }
}
